import {
  ActionCardModel,
  FileQuestionNode,
  IInputNode,
  RecommendationNode,
  ReplacementProcessor,
} from '@playbooks/action-cards-utils'
import sanitizeHtml from 'sanitize-html'

/** Format the visible, completed action-card fields for a play-created email. */
export function getActionCardDataReplacementInfo(actionCardModel: ActionCardModel): string {
  const rows: string[] = []
  const recommendationRows: string[] = []

  for (const node of actionCardModel.getSearchService().filterVisible()) {
    if (node instanceof FileQuestionNode) {
      const fileValues = (node.getValue() ?? [])
        .filter(fileItem => fileItem.id > 0 && !fileItem.error)
        .map(fileItem => {
          const name = escapeHtml(fileItem.name)
          return `<a href="${escapeHtml(fileItem.url || '')}" target="_blank" rel="noopener noreferrer">${name}</a>`
        })

      if (fileValues.length === 0) continue

      rows.push(`<tr><td>${escapeHtml(node.getLabel())}: </td><td>${fileValues.join('; ')}</td></tr>`)
      continue
    }

    if (node instanceof RecommendationNode) {
      if (node.includeInWebPlayNotification) {
        recommendationRows.push(`<tr><td colspan="2">${escapeHtml(node.name)}:</td></tr>`)
        recommendationRows.push(`<tr><td colspan="2">${prepareRecommendationNodeText(node, actionCardModel)}</td></tr>`)
      }
      continue
    }

    const inputNode = node as unknown as IInputNode<unknown>
    if (inputNode.IInputNode && !inputNode.isInputEmpty()) {
      const value = escapeHtml(inputNode.stringifyValue()).replaceAll('\n', '<br/>')
      rows.push(`<tr><td>${escapeHtml(node.getLabel())}: </td><td>${value}</td></tr>`)
    }
  }

  if (recommendationRows.length > 0) {
    if (rows.length > 0) rows.push('<tr><td colspan="2"></td></tr>')
    rows.push(...recommendationRows)
  }

  if (rows.length === 0) return ''

  return `<table style="border-spacing: 2px 4px; width: 100%;"><tbody style="vertical-align: top;">${rows.join('\n')}</tbody></table>`
}

function escapeHtml(text: string): string {
  return text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function prepareRecommendationNodeText(node: RecommendationNode, actionCardModel: ActionCardModel): string {
  const replacementProcessor = new ReplacementProcessor(actionCardModel)

  // Replace all variables in the recommendation text with their corresponding values
  // Applying special markdown handling for widget elements
  const recommendationWithReplacements = replacementProcessor.processReplacements(node.text)

  return sanitizeHtml(recommendationWithReplacements, {
    allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img'],
    allowedAttributes: {
      '*': ['class', 'style'],
      a: ['href', 'name', 'target', 'rel'],
      img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading', 'class', 'style'],
    },
    transformTags: {
      '*': (tagName, attribs) => {
        let style = attribs.style ?? ''

        for (const className of (attribs.class ?? '').split(/\s+/)) {
          const classStyle = cssClassesMap[className]
          if (classStyle) style = mergeStyles(style, classStyle)
        }

        if (tagName === 'img') style = mergeStyles(style, 'max-width: 100%;')
        if (tagName === 'td') style = mergeStyles(style, 'border: 1px solid #e2e2e2')

        return { tagName, attribs: style ? { ...attribs, style } : attribs }
      },
    },
  })
}

function mergeStyles(existingStyle: string, styleToAdd: string): string {
  const normalizedExistingStyle = existingStyle.trim().replace(/;$/, '')
  const normalizedStyleToAdd = styleToAdd.trim()

  if (!normalizedExistingStyle) return normalizedStyleToAdd
  if (!normalizedStyleToAdd) return normalizedExistingStyle
  return `${normalizedExistingStyle}; ${normalizedStyleToAdd}`
}

const cssClassesMap: Record<string, string> = {
  'pl-3': 'padding-left: 12px',
  'p-4': 'padding: 16px',
  'py-2': 'padding-top: 8px; padding-bottom: 8px',
  'py-5': 'padding-top: 20px; padding-bottom: 20px',
  'px-4': 'padding-left: 16px; padding-right: 16px',
  'my-1': 'margin-top: 4px; margin-bottom: 4px',
  'rounded-sm': 'border-radius: 6px',
}
